"use client";

import { Tag } from "@pamperedchef/design-system";
import * as styles from "./ProductHighlightTag.module.scss";

export type HighlightTagColor =
	"default" | "info" | "success" | "warning" | "alert";

/**
 * Owned by this composite rather than imported from the app's catalog domain,
 * so it stays packageable and can be lifted out of consumer-web.
 */
export interface HighlightTag {
	title: string;
	color?: HighlightTagColor;
	icon?: string;
}

export interface ProductHighlightTagProps {
	/**
	 * Promotional badges — Bestseller, Host Exclusive, an award. Callers
	 * select which tags qualify and resolve any date window; this composite
	 * stays presentational.
	 */
	tags: HighlightTag[];
	className?: string;
}

const COLOR_TO_DS_COLOR: Record<
	HighlightTagColor,
	"lime-green" | "info" | "warning" | "error" | "grey"
> = {
	default: "grey",
	info: "info",
	success: "lime-green",
	warning: "warning",
	alert: "error"
};

/**
 * Inline row of `Tag` chips sitting just above (or beside) the price block.
 * Wraps to a second line on narrow viewports.
 */
export function ProductHighlightTag({
	tags,
	className
}: ProductHighlightTagProps) {
	if (!tags.length) return null;

	return (
		<div
			className={[styles["highlight-tag"], className ?? ""]
				.filter(Boolean)
				.join(" ")}
			data-component="ProductHighlightTag"
		>
			{tags.map((tag, idx) => (
				<Tag
					key={`${tag.title}-${idx}`}
					label={tag.title}
					icon={tag.icon ? { name: tag.icon } : undefined}
					kind="subtle"
					size="md"
					color={COLOR_TO_DS_COLOR[tag.color ?? "default"]}
				/>
			))}
		</div>
	);
}
